import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import taskMasterMockup from '../assets/taskmaster_mockup.png';

const fadeIn = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
};

export default function CaseStudyTaskMaster() {
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const [imgLoaded, setImgLoaded] = useState(false);

  // Scroll to top on mount so hero animations always play from the start
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Parallax constraints for Hero Image
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '15%']);

  return (
    <div className="min-h-screen bg-[#000] text-gray-200 overflow-x-hidden selection:bg-cyan-500/30">

      {/* ── Navbar minimal ── */}
      <nav className="fixed top-0 left-0 w-full z-50 py-4 px-6 md:px-12 backdrop-blur-xl border-b border-white/5 flex justify-between items-center bg-[#000]/60">
        <button
          onClick={() => navigate('/')}
          className="group flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <svg className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] font-bold">Back</span>
        </button>
        <div className="opacity-40 font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em]">CASE STUDY / 01</div>
      </nav>

      <main className="pt-32 pb-32">

        {/* ── 1. CINEMATIC HERO STACK ── */}
        <section className="px-4 sm:px-8 md:px-12 max-w-7xl mx-auto flex flex-col items-center text-center mt-12 mb-20 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-3 py-1.5 px-4 mb-10 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-gray-300 font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em]"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_10px_#22d3ee] animate-pulse" />
            Chrome Extension
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, filter: 'blur(10px)', y: 20 }}
            animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="text-[4rem] sm:text-[6rem] md:text-[8rem] lg:text-[10rem] font-black tracking-tighter leading-[0.85] mb-6"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            <span className="text-white block drop-shadow-2xl">TaskMaster</span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-cyan-400 to-purple-500 italic pr-8">Pro</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }}
            className="text-base sm:text-lg lg:text-xl text-gray-400/80 font-light leading-relaxed max-w-2xl mt-8 mb-12"
          >
            Your All-in-One Task Manager. A rigorous exploration into building a zero-trust, privacy-first productivity layout exactly at the limits of local browser constraints.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.5 }}
            className="flex flex-wrap gap-4 justify-center"
          >
            <a href="https://chromewebstore.google.com/detail/ohdbbcionmmidbkalknbaohhdjmlligc?utm_source=item-share-cb" target="_blank" rel="noreferrer" className="group relative inline-flex items-center gap-3 bg-white text-black font-bold text-sm py-4 px-10 rounded-full hover:scale-105 transition-all shadow-[0_0_30px_rgba(255,255,255,0.1)]">
              Chrome Web Store
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
            </a>
            <a href="https://www.youtube.com/watch?v=29AqXUKOOuU" target="_blank" rel="noreferrer" className="inline-flex items-center gap-3 border border-white/10 bg-[#111] hover:bg-[#222] text-white font-bold text-sm py-4 px-10 rounded-full hover:scale-105 transition-all">
              Watch Demo
            </a>
          </motion.div>
        </section>

        {/* ── 2. IMMERSIVE HERO IMAGE ── */}
        <section className="relative w-full h-[60vh] md:h-[85vh] overflow-hidden mb-32 border-y border-white/5 bg-[#050505] flex items-center justify-center">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(6,182,212,0.1)_0%,transparent_50%)] z-10 pointer-events-none" />
          
          {/* Loader */}
          {!imgLoaded && (
            <div className="absolute z-15 flex flex-col items-center justify-center space-y-4">
              <div className="w-12 h-12 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin"></div>
              <span className="font-mono text-[10px] text-cyan-500 uppercase tracking-widest animate-pulse">Loading Visuals...</span>
            </div>
          )}

          <motion.img
            onLoad={() => setImgLoaded(true)}
            style={{ scale: imgScale, y: imgY }}
            src={taskMasterMockup}
            alt="TaskMaster Pro Layout"
            className={`absolute inset-0 w-full h-full object-cover object-center filter saturate-110 opacity-70 transition-opacity duration-1000 ${imgLoaded ? 'opacity-70' : 'opacity-0'}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#000] via-transparent to-[#000] z-20 pointer-events-none" />
        </section>

        {/* ── 3. BENTO METRICS ── */}
        <section className="px-4 sm:px-8 md:px-12 max-w-7xl mx-auto mb-40 relative z-20">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
          >
            {[
              { label: 'ROLE', value: 'Creator & Engineer' },
              { label: 'PLATFORM', value: 'Google Chrome' },
              { label: 'ACTIVE USERS', value: '377+' },
              { label: 'TECH STACK', value: 'HTML, CSS, JS' }
            ].map((stat, i) => (
              <motion.div
                variants={fadeIn} key={i}
                className="p-8 md:p-10 rounded-[2rem] bg-[#080808] border border-white/5 flex flex-col justify-end min-h-[160px] group hover:border-cyan-500/30 transition-colors"
              >
                <p className="text-gray-500 font-mono text-[10px] uppercase tracking-widest mb-3 group-hover:text-cyan-400 transition-colors">{stat.label}</p>
                <p className="text-white font-bold text-lg sm:text-xl tracking-wide">{stat.value}</p>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* ── 4. THE NARRATIVE (PROBLEM VS SOLUTION) ── */}
        <section className="px-4 sm:px-8 md:px-12 max-w-6xl mx-auto mb-40">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-24">
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
              <div className="flex items-center gap-3 mb-8">
                <span className="w-8 h-[1px] bg-red-500/50"></span>
                <span className="text-red-400 font-mono text-[10px] tracking-[0.2em] font-bold uppercase">The Friction</span>
              </div>
              <h2 className="text-3xl lg:text-5xl font-black mb-8 leading-[1.1] text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
                Extensions demand <br /><span className="text-gray-500">terrifying access.</span>
              </h2>
              <p className="text-gray-400 text-lg leading-loose font-light">
                Most productivity extensions on the market demand horrifying levels of data access—reading your browsing history, requiring cloud account logins, and syncing personal tasks to external servers. This creates a massive friction point for professionals who need a safe, isolated tool.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}>
              <div className="flex items-center gap-3 mb-8">
                <span className="w-8 h-[1px] bg-cyan-500/50"></span>
                <span className="text-cyan-400 font-mono text-[10px] tracking-[0.2em] font-bold uppercase">The Solution</span>
              </div>
              <h2 className="text-3xl lg:text-5xl font-black mb-8 leading-[1.1] text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
                Architecture of <br /><span className="text-cyan-400">Absolute Trust.</span>
              </h2>
              <p className="text-gray-400 text-lg leading-loose font-light">
                I built TaskMaster Pro to guarantee 100% safety. By strictly leveraging local browser storage and entirely stripping out external server dependencies, the extension proves data privacy is possible. Your tasks literally cannot leave your browser.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ── 5. FEATURES ── */}
        <section className="px-4 sm:px-8 md:px-12 max-w-6xl mx-auto mb-40">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
            className="grid md:grid-cols-3 gap-6"
          >
            {[
              { icon: '⏳', title: 'Timer Integration', desc: 'Embedded timers to track tasks via Pomodoro logic, directly on schedule.' },
              { icon: '📅', title: 'Time Logic', desc: 'Attach strict deadlines. Expiry visuals instantly alert you when deadlines pass.' },
              { icon: '⚡', title: 'Frictionless', desc: 'Mark tasks complete with a satisfying visual toggle. Instant-undo supported.' }
            ].map((feat, i) => (
              <motion.div variants={fadeIn} key={i} className="bg-[#050505] border border-white/5 p-10 rounded-[2.5rem] hover:bg-[#0a0a0a] transition-colors group">
                <div className="w-16 h-16 rounded-full bg-[#111] border border-white/10 flex items-center justify-center text-3xl mb-8 group-hover:scale-110 group-hover:-rotate-6 transition-transform duration-500">
                  {feat.icon}
                </div>
                <h4 className="text-2xl font-bold text-white mb-4 tracking-tight">{feat.title}</h4>
                <p className="text-gray-500 font-light leading-relaxed">{feat.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* ── 6. TECHNICAL DEEP DIVE (CODE SNIPPET) ── */}
        <section className="px-4 sm:px-8 md:px-12 max-w-5xl mx-auto mb-20">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl md:text-6xl font-black mb-16 text-center text-white" style={{ fontFamily: "'Syne', sans-serif" }}>Engineering V3.</h2>

            <div className="bg-[#080808] border border-white/10 rounded-[2.5rem] overflow-hidden shadow-2xl flex flex-col lg:flex-row">
              {/* Left text */}
              <div className="p-10 lg:p-16 lg:w-1/2 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-white/5">
                <div className="inline-flex items-center gap-2 py-1 px-3 mb-8 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 font-mono text-[10px] uppercase tracking-widest">
                  chrome.storage.local
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6">Zero-Trust Data Flow</h3>
                <p className="text-gray-400 font-light leading-relaxed mb-6">
                  Under Manifest V3’s strict Content Security Policy, we enforce data locking. Tasks are serialized securely into local storage through background service workers logic, ensuring instantaneous UI loads across tabs.
                </p>
                <div className="flex gap-4">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_10px_#22d3ee]"></span>
                  <span className="w-2 h-2 rounded-full bg-purple-400 shadow-[0_0_10px_#c084fc]"></span>
                </div>
              </div>

              {/* Right Code Block */}
              <div className="p-8 lg:p-12 lg:w-1/2 bg-[#020202] relative group">
                <div className="absolute top-4 right-4 bg-white/5 border border-white/10 rounded px-2 py-1 text-[10px] font-mono text-gray-500">background.js</div>
                <pre className="font-mono text-xs sm:text-sm leading-8 overflow-x-auto text-gray-300">
                  <code>
                    <span className="text-purple-400">const</span> syncTasks = <span className="text-blue-400">async</span> (payload) <span className="text-blue-400">=&gt;</span> {'{'}
                    <br />  <span className="text-gray-500 italic">// Enforce local environment</span>
                    <br />  <span className="text-cyan-400">chrome</span>.<span className="text-blue-300">storage</span>.<span className="text-blue-300">local</span>.<span className="text-yellow-200">set</span>({'{'}
                    <br />    tasks: payload.tasks
                    <br />  {'}'}, () <span className="text-blue-400">=&gt;</span> {'{'}
                    <br />    <span className="text-purple-400">console</span>.<span className="text-yellow-200">log</span>(<span className="text-green-300">'State locally preserved.'</span>);
                    <br />  {'}'});
                    <br />{'}'};
                    <br />
                    <br /><span className="text-gray-500 italic">// Manifest V3 Listener logic</span>
                    <br /><span className="text-cyan-400">chrome</span>.<span className="text-blue-300">runtime</span>.<span className="text-blue-300">onMessage</span>.<span className="text-yellow-200">addListener</span>((req) <span className="text-blue-400">=&gt;</span> {'{'}
                    <br />  <span className="text-purple-400">if</span> (req.type === <span className="text-green-300">'SYNC'</span>) syncTasks(req);
                    <br />{'}'});
                  </code>
                </pre>
              </div>
            </div>
          </motion.div>
        </section>

      </main>
    </div>
  );
}
